import { SalesResponse } from "../../../hooks/useGetSales";

export interface EditedValues {
  servicePrice?: number | null;
  serviceTotal?: number | null;
  finalTotal?: number | null;
  recurringFee?: number | null;
  time?: number | null;
  isServiceLineTotalOverridden?: boolean;
  isGlobalTotalOverridden?: boolean;
}

/** When true (invoice edit), unset line fields default to 0 instead of sales-derived values. */
export type PricingOptions = { zeroLineDefaults?: boolean };

export const roundMoney = (n: number) => Math.round(n * 100) / 100;

/** Effective unit price and computed main line total (before line-level override). */
export const getMainLineComputedPricing = (
  salesData: SalesResponse | undefined,
  editedValues: Pick<EditedValues, "time" | "servicePrice">,
  options?: PricingOptions,
): { effectiveUnitPrice: number; computedLineTotal: number } => {
  if (!salesData) {
    return { effectiveUnitPrice: 0, computedLineTotal: 0 };
  }
  const backendMinutes = salesData.mainAreas?.time?.minutes ?? 0;
  const backendMainTotal = salesData.mainAreas?.cost ?? 0;
  const backendHours = backendMinutes / 60;
  const baseHourlyRate = backendHours > 0 ? backendMainTotal / backendHours : 0;
  const zero = options?.zeroLineDefaults === true;
  const minutes = zero ? (editedValues.time ?? 0) : editedValues.time != null ? editedValues.time : backendMinutes;
  const effectiveUnitPrice = zero
    ? (editedValues.servicePrice ?? 0)
    : editedValues.servicePrice != null
      ? editedValues.servicePrice
      : baseHourlyRate;
  const computedLineTotal = roundMoney((minutes / 60) * effectiveUnitPrice);
  return { effectiveUnitPrice, computedLineTotal };
};

/** Main service line total (qty x unit price or override). */
export const computeMainAreasLineTotal = (
  salesData: SalesResponse | undefined,
  editedValues: EditedValues,
  options?: PricingOptions,
): number => {
  if (!salesData) return 0;

  const backendMinutes = salesData.mainAreas?.time?.minutes ?? 0;
  const backendMainTotal = salesData.mainAreas?.cost ?? 0;
  const backendHours = backendMinutes / 60;
  const baseHourlyRate = backendHours > 0 ? backendMainTotal / backendHours : 0;

  const zero = options?.zeroLineDefaults === true;
  const minutes = zero ? (editedValues.time ?? 0) : editedValues.time != null ? editedValues.time : backendMinutes;
  const rate = zero
    ? (editedValues.servicePrice ?? 0)
    : editedValues.servicePrice != null
      ? editedValues.servicePrice
      : baseHourlyRate;

  const computedMainTotal = roundMoney((minutes / 60) * rate);

  return editedValues.isServiceLineTotalOverridden === true && editedValues.serviceTotal != null
    ? editedValues.serviceTotal
    : computedMainTotal;
};

/** Grand total from current line + other areas + fees, ignoring global total override. */
export const calculateComputedGrandTotal = (
  salesData: SalesResponse | undefined,
  editedValues: EditedValues,
  options?: PricingOptions,
): number => {
  if (!salesData) return 0;

  const zero = options?.zeroLineDefaults === true;
  const mainAreasTotal = computeMainAreasLineTotal(salesData, editedValues, options);

  const recurringFeeValue = zero
    ? (editedValues.recurringFee ?? 0)
    : editedValues.recurringFee != null
      ? editedValues.recurringFee
      : (salesData.increasePercentageByFrequency ?? 0);

  const otherAreasCost = zero ? 0 : (salesData.otherAreas?.cost ?? 0);
  const additionalObjectsCost = zero ? 0 : (salesData.additionalObjects?.cost ?? 0);
  const specialRequestsCost = zero ? 0 : (salesData.specialRequests ?? 0);

  return roundMoney(
    mainAreasTotal + otherAreasCost + additionalObjectsCost + specialRequestsCost + recurringFeeValue,
  );
};

/**
 * Displayed grand total: respects global override when set.
 */
export const calculateTotalFromSalesData = (
  salesData: SalesResponse | undefined,
  editedValues: EditedValues,
  options?: PricingOptions,
): number => {
  if (!salesData) return 0;

  if (editedValues.isGlobalTotalOverridden === true && editedValues.finalTotal != null) {
    return roundMoney(editedValues.finalTotal);
  }

  return calculateComputedGrandTotal(salesData, editedValues, options);
};
